import { Avatar, Button, cn, Divider, Typography } from "@suraasa/placebo-ui"
import { CoverStyle } from "api/resources/profile/types"
import clsx from "clsx"
import SuraasaPlusBadge, { PlusBadgeSize } from "components/SuraasaPlusBadge"
import { AuthManager } from "features/Authentication/AuthManager"
import { HOME_ONBOARDING_TARGETS } from "features/Home/HomeOnboarding/constants"
import { DEFAULT_PROFILE_COVER, getCoverImageURL } from "features/Profile/utils"
import { useSuraasaPlusContext } from "features/SuraasaPlus/context"
import { context } from "global/Context/context"
import SwitchGrowthspace from "growthspace/SwitchGrowthspace"
import { useContext, useState } from "react"
import { Link, useLocation, useNavigate } from "react-router-dom"
import routes from "routes"
import { getPlatformURL } from "utils/helpers"
import { trackElementOnPH } from "utils/tracking"

import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/HoverCard"

const profileDefinedRoutes = [
  routes.profile.self,
  routes.certificates,
  routes.mySchedule,
  routes.orders,
  routes.settings,
]
enum MenuOptions {
  suraasaProfile = "My Profile",
  myCertificates = "My Certificates",
  mySchedule = "My Schedule",
  ordersAndPayments = "Orders and Payments",
  settings = "Settings",
  helpCenter = "Help Center",
  signOut = "Sign Out",
}

const menuLink = [
  { label: MenuOptions.myCertificates, link: routes.certificates },
  { label: MenuOptions.mySchedule, link: routes.mySchedule },
  { label: MenuOptions.ordersAndPayments, link: routes.orders },
  { label: MenuOptions.settings, link: routes.settings },
  {
    label: MenuOptions.helpCenter,
    link: getPlatformURL("sso", "/help"),
  },
  { label: MenuOptions.signOut, onClick: () => AuthManager.redirectToLogout() },
]

const ProfileMenuButton = ({
  coverStyle,
  src,
  name,
}: {
  coverStyle?: CoverStyle
  src: string | null
  name: string
}) => {
  const location = useLocation()
  const active = profileDefinedRoutes.includes(location.pathname)
  const [open, setOpen] = useState(false)
  const { isActive } = useSuraasaPlusContext()

  const navigate = useNavigate()

  const toggleHover = (state: boolean) => {
    setOpen(state)
  }

  const { growthspaces } = useContext(context)

  return (
    <HoverCard
      openDelay={100}
      closeDelay={100}
      onOpenChange={toggleHover}
      open={open}
    >
      <HoverCardTrigger
        onClick={() => {
          toggleHover(true)
          navigate(routes.profile.self)
        }}
        asChild
      >
        <Button
          variant="text"
          className={clsx(
            "flex flex-col items-center py-0.75 text-onSurface-500 focus:!ring-0",
            {
              "!bg-primary-50": active,
              relative: isActive,
            },
            HOME_ONBOARDING_TARGETS.profile
          )}
        >
          <Avatar
            src={src}
            name={name}
            className={clsx("size-4 !border border-onSurface-200", {
              "border-primary-600": active,
              "border-surface shadow-[0px_3.93px_9.82px_0px_rgba(0,0,0,0.05)]":
                isActive,
            })}
          />
          {isActive && (
            <SuraasaPlusBadge
              size={PlusBadgeSize.small}
              className="absolute bottom-0.25"
            />
          )}
        </Button>
      </HoverCardTrigger>
      <HoverCardContent className="w-[266px] rounded-lg border border-onSurface-200 bg-white">
        <Link
          className="block w-full px-0.5 pt-0.5"
          onClick={() => {
            toggleHover(false)
          }}
          to={routes.profile.self}
          {...trackElementOnPH({
            name: "View Profile",
            id: "profile-nav-card-item",
          })}
        >
          <div
            style={{
              backgroundImage: `url(${getCoverImageURL(
                coverStyle || DEFAULT_PROFILE_COVER,
                true
              )})`,
            }}
            className="h-[65px] w-full rounded bg-cover text-black"
          />
          <div className="flex flex-col items-center">
            <div className="relative mb-1 mt-[-29px]">
              <Avatar
                src={src}
                name={name}
                className={clsx("size-9", {
                  "relative shadow-[0px_3.93px_9.82px_0px_rgba(0,0,0,0.05)] border-surface":
                    isActive,
                })}
              />
              {isActive && (
                <SuraasaPlusBadge
                  size={PlusBadgeSize.medium}
                  className="absolute -bottom-1 left-1/2 -translate-x-1/2"
                />
              )}
            </div>
            <Typography
              variant="title4"
              className={clsx("mb-0.5 w-full px-3 text-center", {
                "mt-1": isActive,
              })}
              title={name}
            >
              {name}
            </Typography>
            <Typography variant="smallBody" className="text-onSurface-500">
              View Profile
            </Typography>
          </div>
        </Link>
        <Divider className="my-1 bg-onSurface-200" />
        <div className="flex flex-col gap-0.25 px-1">
          {menuLink.map(route => {
            const styles = cn(
              "w-full rounded p-1 text-start text-onSurface-900 hover:bg-[#F1F5F9]",
              {
                "text-critical-500 hover:bg-critical-50":
                  route.label === MenuOptions.signOut,
                "bg-primary-50 text-primary-500":
                  route.link === location.pathname,
              }
            )

            if (route.onClick) {
              return (
                <button
                  key={route.label}
                  onClick={() => {
                    route.onClick()
                    toggleHover(false)
                  }}
                  className={styles}
                >
                  <Typography variant="strongSmallBody">
                    {route.label}
                  </Typography>
                </button>
              )
            }

            return (
              <Link
                key={route.label}
                to={route.link}
                onClick={() => {
                  toggleHover(false)
                }}
                {...trackElementOnPH({
                  name: route.label,
                  id: "profile-nav-card-item",
                })}
              >
                <button className={styles}>
                  <Typography variant="strongSmallBody">
                    {route.label}
                  </Typography>
                </button>
              </Link>
            )
          })}
        </div>
        {growthspaces.length > 1 && (
          <>
            <Divider className="mt-1 bg-onSurface-200" />
            <SwitchGrowthspace openOnHover />
          </>
        )}
        <Divider className="my-1 bg-onSurface-200" />
        <div className="flex items-center justify-start gap-0.5 pb-0.25 pl-2 text-left text-xs font-normal leading-[15.6px] text-onSurface-500">
          <Link
            className="hover:underline"
            to={getPlatformURL("suraasa", "/terms-of-use")}
          >
            Terms and Conditions
          </Link>
          <span>•</span>
          <Link
            className="hover:underline"
            to={getPlatformURL("suraasa", "/privacy-policy")}
          >
            Privacy Policy
          </Link>
        </div>
        <Typography className="pb-0.5 pl-2 !text-[0.75rem] text-onSurface-400">
          v{APP_VERSION}
        </Typography>
      </HoverCardContent>
    </HoverCard>
  )
}

export default ProfileMenuButton
